import { forwardRef, useImperativeHandle, useState } from "react";

import { queryClient } from "App";
import CustomTableRowService from "services/CustomTableRow";
import { customTableRowsKey } from "routes/custom_tables/grid/queryKeys";
import { formatError } from "routes/custom_tables/helpers";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

const SYSTEM_KEYS = new Set(["id", "created_at", "updated_at"]);

const hasPreviewValue = v =>
  v != null && v !== "" && !(Array.isArray(v) && v.length === 0);

// Label each row by the table's display column when one is set, else its
// first non-empty user column so sparse rows don't render as "null". The
// id always leads so rows stay identifiable even with duplicate labels.
const previewLine = (row, displayFieldId) => {
  let value = null;
  if (displayFieldId) {
    const raw = row[`${displayFieldId}__display`]?.label ?? row[displayFieldId];
    if (hasPreviewValue(raw)) value = raw;
  }
  if (value == null) {
    const entry = Object.entries(row).find(
      ([key, v]) =>
        !SYSTEM_KEYS.has(key) &&
        !key.endsWith("__display") &&
        hasPreviewValue(v)
    );
    if (entry) [, value] = entry;
  }
  if (value == null) return `#${row.id}`;
  return `#${row.id} — ${Array.isArray(value) ? value.join(", ") : String(value)}`;
};

const DeleteRowsModal = forwardRef((_props, ref) => {
  const [open, setOpen] = useState(false);
  const [slug, setSlug] = useState(null);
  const [ids, setIds] = useState([]);
  const [rowsPreview, setRowsPreview] = useState([]);
  const [displayFieldId, setDisplayFieldId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [onSuccess, setOnSuccess] = useState(null);

  useImperativeHandle(ref, () => ({
    open: ({ slug, ids, rowsPreview, displayFieldId, onSuccess }) => {
      setSlug(slug);
      setIds(ids);
      setRowsPreview(rowsPreview ?? []);
      setDisplayFieldId(displayFieldId ?? null);
      setError(null);
      setOnSuccess(() => onSuccess ?? null);
      setOpen(true);
    },
    close: () => setOpen(false),
  }));

  const handleClose = () => {
    if (loading) return;
    setOpen(false);
  };

  const handleConfirm = async () => {
    if (!slug || ids.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      await CustomTableRowService.bulkDelete(slug, ids);
      await queryClient.invalidateQueries({
        queryKey: customTableRowsKey(slug),
      });
      setOpen(false);
      onSuccess?.();
    } catch (err) {
      setError(formatError(err, "Failed to delete rows"));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const label = `Delete ${ids.length} row${ids.length === 1 ? "" : "s"}`;

  return (
    <Dialog
      open={open}
      onOpenChange={o => {
        if (!o) handleClose();
      }}
    >
      <DialogContent aria-label="Delete rows">
        <DialogTitle>{label}</DialogTitle>
        <div>
          <p>The following rows will be deleted:</p>
          <ul className="tw:list-none tw:pl-0 tw:my-2 tw:max-h-50 tw:overflow-y-auto tw:border tw:border-[var(--color-border)] tw:rounded-sm tw:bg-[var(--color-warm-gray-light)]">
            {rowsPreview.map(row => (
              <li
                key={row.id}
                className="tw:px-3 tw:py-1.5 tw:border-b tw:border-[var(--color-border)] tw:text-[var(--color-text-primary)] tw:text-sm tw:last:border-b-0"
              >
                {previewLine(row, displayFieldId)}
              </li>
            ))}
          </ul>
          {error && (
            <div className="tw:mt-3 tw:text-sm tw:text-destructive">
              {error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading && <Spinner data-icon="inline-start" />}
            {label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

DeleteRowsModal.displayName = "DeleteRowsModal";

export default DeleteRowsModal;
